import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';

import {
  SkillAdminHelpRow,
  SkillGroup,
  SkillLearningHttpError,
  SkillSpecializationDeactivateParams,
  SkillSpecializationFolderId,
  SkillSpecializationListItem,
  SkillSpecializationSourceGroup,
} from '../../../models/skill-learning.model';
import {
  SKILL_CONFIGURATION_ERROR,
  SKILL_DEACTIVATION_MESSAGE,
  SKILL_DEPARTMENT_QUERY_PARAM,
  SKILL_HTTP_STATUS,
  SKILL_LEARNING_EDIT_PAGE,
  SKILL_LEARNING_EMPTY_TEXT,
  SKILL_LEARNING_FIRST_INDEX,
  SKILL_LEARNING_LEVEL_COUNT,
  SKILL_LEARNING_NOT_FOUND_INDEX,
  SKILL_LEARNING_ROUTE,
  SKILL_LEARNING_ROUTE_PART,
  SKILL_LEARNING_SIDEBAR,
  SKILL_SPECIALIZATION_DEFAULT_FOLDER,
  SKILL_SPECIALIZATION_FOLDER,
  SKILL_SPECIALIZATION_FOLDER_LABEL,
  SKILL_SPECIALIZATION_FOLDER_ORDER,
  SKILL_URL_QUERY_SEPARATOR,
  SKILL_VALUE_TYPE,
} from '../../../options/skill-learning.option';
import { SkillSpecializationFacadeService } from '../../../services/skill-specialization-facade.service';
import { SkillRoleService } from '../../../services/skill-role.service';
import { ToastService } from '../../../../../../services/toast.service';
import { SidebarService } from '../../../../../../services/side-bar.service';

const ADMIN_HELP_ROWS: SkillAdminHelpRow[] = [
  { titleKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_CREATE_TITLE', textKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_CREATE_TEXT' },
  { titleKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_FILL_TITLE', textKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_FILL_TEXT' },
  { titleKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_REVIEW_TITLE', textKey: 'SKILL_LEARNING_ADMIN_LIST_HELP_REVIEW_TEXT' },
];

@Component({
  selector: 'app-skill-specialization-list-container',
  templateUrl: './specialization-list-container.component.html',
  styleUrls: ['./specialization-list-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSpecializationListContainerComponent implements OnInit, OnDestroy {

  specializations: SkillSpecializationListItem[] = [];
  catalogSpecializations: SkillSpecializationListItem[] = [];
  activeFolderSpecializations: SkillSpecializationListItem[] = [];
  otherSourceGroups: SkillSpecializationSourceGroup[] = [];
  activeSourceDepartmentId: string | null = null;
  activeFolderId = SKILL_SPECIALIZATION_DEFAULT_FOLDER;
  readonly folderIds = SKILL_SPECIALIZATION_FOLDER;
  readonly folderOrder = SKILL_SPECIALIZATION_FOLDER_ORDER;
  readonly folderLabels = SKILL_SPECIALIZATION_FOLDER_LABEL;

  pendingDeleteItem: SkillSpecializationListItem | null = null;
  isDeleting = false;
  deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
  isLoading = false;
  hasLoadError = false;
  isListDenied = false;
  isCatalogLoading = false;
  hasCatalogError = false;
  isCatalogDenied = false;
  canCreate = false;
  availableGroups: SkillGroup[] = [];
  selectedCatalogDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  fixedCatalogDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
  readonly adminHelpRows = ADMIN_HELP_ROWS;

  private ownSpecializations: SkillSpecializationListItem[] = [];
  private crossSpecializations: SkillSpecializationListItem[] = [];
  private companySpecializations: SkillSpecializationListItem[] = [];
  private readonly destroy$ = new Subject<void>();
  private listRequestId = SKILL_LEARNING_FIRST_INDEX;
  private catalogRequestId = SKILL_LEARNING_FIRST_INDEX;
  private isCreateAccessLoaded = false;
  private pendingCatalogDepartmentSelection: string | null = null;

  constructor(
    private facade: SkillSpecializationFacadeService,
    private roleService: SkillRoleService,
    private toastService: ToastService,
    private sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
  ) {}

  get shouldShowNoAssignedSpecializations(): boolean {
    return !this.canShowCatalog && this.roleService.isSpecializationAdmin()
      && !this.hasLoadError && !this.isListDenied && !this.canCreate
      && this.specializations.length === SKILL_LEARNING_FIRST_INDEX;
  }

  get canShowCatalog(): boolean {
    return this.availableGroups.length > SKILL_LEARNING_FIRST_INDEX;
  }

  get selectedCatalogDepartment(): SkillGroup | null {
    return this.availableGroups.find(group => group.id === this.selectedCatalogDepartmentId) || null;
  }

  get shouldShowCatalogDepartmentSelector(): boolean {
    return !this.fixedCatalogDepartmentId || !this.selectedCatalogDepartment;
  }

  get isFolderLoading(): boolean {
    return this.isLoading || this.isCatalogLoading;
  }

  get hasFolderError(): boolean {
    return this.hasLoadError || this.hasCatalogError;
  }

  get isFolderDenied(): boolean {
    return this.isListDenied || this.isCatalogDenied;
  }

  get pendingDeleteId(): string | null {
    return this.pendingDeleteItem ? this.pendingDeleteItem.id : null;
  }

  get pendingDeleteName(): string {
    return this.pendingDeleteItem ? this.pendingDeleteItem.name : SKILL_LEARNING_EMPTY_TEXT;
  }

  get canConfirmDelete(): boolean {
    return !!this.pendingDeleteItem
      && !!this.pendingDeleteItem.canDeactivate
      && typeof this.pendingDeleteItem.configurationRevision === SKILL_VALUE_TYPE.NUMBER
      && !this.isDeleting;
  }

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption(SKILL_LEARNING_SIDEBAR.SPECIALIZATIONS);
    this.subscribeToCatalogDepartment();
    this.loadCreateAccess();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEditRequested(id: string): void {
    this.router.navigate([
      SKILL_LEARNING_ROUTE.SPECIALIZATIONS,
      id,
      SKILL_LEARNING_ROUTE_PART.EDIT,
      SKILL_LEARNING_EDIT_PAGE.GENERAL,
    ], { queryParams: this.catalogDepartmentQuery });
  }

  onCatalogDepartmentChanged(departmentId: string): void {
    if (this.fixedCatalogDepartmentId) {
      return;
    }
    const nextDepartmentId = departmentId || SKILL_LEARNING_EMPTY_TEXT;
    if (nextDepartmentId === this.selectedCatalogDepartmentId) {
      return;
    }
    this.applyCatalogDepartmentContext(nextDepartmentId);
    this.pendingCatalogDepartmentSelection = nextDepartmentId;
    this.updateCatalogDepartmentQuery();
  }

  onFolderChanged(folderId: SkillSpecializationFolderId): void {
    if (this.folderOrder.indexOf(folderId) === SKILL_LEARNING_NOT_FOUND_INDEX
      || this.activeFolderId === folderId) {
      return;
    }
    this.activeFolderId = folderId;
    this.refreshActiveFolder();
    this.cdr.markForCheck();
  }

  onSourceDepartmentChanged(groupId: string): void {
    if (!this.otherSourceGroups.some(group => group.groupId === groupId)) {
      return;
    }
    this.activeSourceDepartmentId = groupId;
    this.refreshActiveFolder();
    this.cdr.markForCheck();
  }

  onCatalogRetry(): void {
    if (this.selectedCatalogDepartmentId) {
      this.loadSelectedDepartmentSources();
    }
  }

  trackByGroupId(index: number, group: SkillGroup): string {
    return group.id;
  }

  trackByFolderId(index: number, folderId: SkillSpecializationFolderId): string {
    return folderId;
  }

  trackBySourceGroupId(index: number, group: SkillSpecializationSourceGroup): string {
    return group.groupId;
  }

  trackByHelpRow(index: number, row: SkillAdminHelpRow): string {
    return row.titleKey;
  }

  onDeleteRequested(item: SkillSpecializationListItem): void {
    if (!item || !item.canDeactivate) {
      return;
    }
    this.pendingDeleteItem = item;
    this.deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.cdr.markForCheck();
  }

  onCreateRequested(): void {
    this.router.navigate([
      SKILL_LEARNING_ROUTE.SPECIALIZATIONS,
      SKILL_LEARNING_ROUTE_PART.CREATE,
    ]);
  }

  onFillTaskRequested(id: string): void {
    this.facade.createFillTask(id)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleFillTaskSuccess(),
        () => this.handleFillTaskError(),
      );
  }

  confirmDelete(): void {
    if (!this.canConfirmDelete || !this.pendingDeleteItem) {
      return;
    }
    const item = this.pendingDeleteItem;
    const params: SkillSpecializationDeactivateParams = {
      expectedRevision: item.configurationRevision as number,
    };
    if (this.selectedCatalogDepartmentId) {
      params.departmentId = this.selectedCatalogDepartmentId;
    }
    this.isDeleting = true;
    this.deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.facade.deleteSpecialization(item.id, params)
      .takeUntil(this.destroy$)
      .subscribe(
        () => this.handleDeleteSuccess(item.id),
        error => this.handleDeleteError(error),
      );
  }

  cancelDelete(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteItem = null;
    this.deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.cdr.markForCheck();
  }

  private subscribeToCatalogDepartment(): void {
    this.router.routerState.root.queryParams
      .takeUntil(this.destroy$)
      .subscribe(params => this.handleRouteDepartmentChange(
        params[SKILL_DEPARTMENT_QUERY_PARAM] || SKILL_LEARNING_EMPTY_TEXT,
      ));
  }

  private handleRouteDepartmentChange(departmentId: string): void {
    if (!this.isCreateAccessLoaded) {
      this.selectedCatalogDepartmentId = departmentId;
      return;
    }
    const isManualSelection = departmentId === this.pendingCatalogDepartmentSelection;
    this.pendingCatalogDepartmentSelection = null;
    const fixedDepartment = isManualSelection ? null : this.resolveFixedCatalogDepartment(departmentId);
    this.fixedCatalogDepartmentId = fixedDepartment ? fixedDepartment.id : SKILL_LEARNING_EMPTY_TEXT;
    const nextDepartmentId = fixedDepartment ? fixedDepartment.id : departmentId;
    if (nextDepartmentId !== this.selectedCatalogDepartmentId) {
      this.applyCatalogDepartmentContext(nextDepartmentId);
    }
    if (nextDepartmentId !== departmentId) {
      this.updateCatalogDepartmentQuery(true);
    }
    this.cdr.markForCheck();
  }

  private applyCatalogDepartmentContext(departmentId: string): void {
    this.selectedCatalogDepartmentId = departmentId;
    this.clearContextData();
    if (!this.canShowCatalog) {
      this.loadSpecializations();
      return;
    }
    if (this.selectedCatalogDepartmentId) {
      this.loadSelectedDepartmentSources();
    }
  }

  private clearContextData(): void {
    this.listRequestId += SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.catalogRequestId += SKILL_LEARNING_LEVEL_COUNT.ONE;
    this.pendingDeleteItem = null;
    this.deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.specializations = [];
    this.catalogSpecializations = [];
    this.isLoading = false;
    this.isCatalogLoading = false;
    this.hasLoadError = false;
    this.hasCatalogError = false;
    this.isListDenied = false;
    this.isCatalogDenied = false;
    this.activeFolderId = SKILL_SPECIALIZATION_DEFAULT_FOLDER;
    this.activeSourceDepartmentId = null;
    this.resetDerivedFolders();
  }

  private loadSelectedDepartmentSources(): void {
    this.loadSpecializations();
    this.loadCatalog();
  }

  private loadSpecializations(): void {
    const requestId = ++this.listRequestId;
    this.isLoading = true;
    this.hasLoadError = false;
    this.isListDenied = false;
    const departmentId = this.selectedCatalogDepartmentId || undefined;
    this.facade.fetchSpecializationList(departmentId)
      .takeUntil(this.destroy$)
      .subscribe(
        list => this.handleLoadSuccess(list, requestId),
        error => this.handleLoadError(error, requestId),
      );
  }

  private loadCatalog(): void {
    if (!this.selectedCatalogDepartmentId) {
      return;
    }
    const requestId = ++this.catalogRequestId;
    this.isCatalogLoading = true;
    this.hasCatalogError = false;
    this.isCatalogDenied = false;
    this.facade.fetchAvailableSpecializations(this.selectedCatalogDepartmentId)
      .takeUntil(this.destroy$)
      .subscribe(
        list => this.handleCatalogLoaded(list, requestId),
        error => this.handleCatalogError(error, requestId),
      );
  }

  private handleLoadSuccess(list: SkillSpecializationListItem[], requestId: number): void {
    if (requestId !== this.listRequestId) {
      return;
    }
    this.specializations = list || [];
    this.syncPendingDeleteItem();
    this.isLoading = false;
    this.hasLoadError = false;
    this.isListDenied = false;
    this.rebuildFolders();
    this.cdr.markForCheck();
  }

  private handleLoadError(error: SkillLearningHttpError, requestId: number): void {
    if (requestId !== this.listRequestId) {
      return;
    }
    this.specializations = [];
    this.pendingDeleteItem = null;
    this.isLoading = false;
    this.hasLoadError = true;
    this.isListDenied = !!error && error.status === SKILL_HTTP_STATUS.FORBIDDEN;
    this.rebuildFolders();
    if (!this.isListDenied && !this.canShowCatalog) {
      this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_LOAD_ERROR'));
    }
    this.cdr.markForCheck();
  }

  private handleCatalogLoaded(list: SkillSpecializationListItem[], requestId: number): void {
    if (requestId !== this.catalogRequestId) {
      return;
    }
    this.catalogSpecializations = list || [];
    this.isCatalogLoading = false;
    this.hasCatalogError = false;
    this.isCatalogDenied = false;
    this.rebuildFolders();
    this.cdr.markForCheck();
  }

  private handleCatalogError(error: SkillLearningHttpError, requestId: number): void {
    if (requestId !== this.catalogRequestId) {
      return;
    }
    this.catalogSpecializations = [];
    this.isCatalogLoading = false;
    this.hasCatalogError = true;
    this.isCatalogDenied = !!error && error.status === SKILL_HTTP_STATUS.FORBIDDEN;
    this.rebuildFolders();
    this.cdr.markForCheck();
  }

  private rebuildFolders(): void {
    this.resetDerivedFolders();
    this.buildUniqueSpecializations().forEach(item => this.classifySpecialization(item));
    this.ensureActiveSourceDepartment();
    this.refreshActiveFolder();
  }

  private buildUniqueSpecializations(): SkillSpecializationListItem[] {
    const seen: { [id: string]: boolean } = {};
    return this.specializations.concat(this.catalogSpecializations).filter(item => {
      if (!item || !item.id || seen[item.id]) {
        return false;
      }
      seen[item.id] = true;
      return true;
    });
  }

  private classifySpecialization(item: SkillSpecializationListItem): void {
    if (item.probationRequiredByCompany || item.probationRequiredByDepartment) {
      this.companySpecializations.push(item);
    } else if (item.crossDepartmental) {
      this.crossSpecializations.push(item);
    } else if (this.isOwnDepartmentSpecialization(item)) {
      this.ownSpecializations.push(item);
    } else {
      this.addOtherSpecialization(item);
    }
  }

  private addOtherSpecialization(item: SkillSpecializationListItem): void {
    const groupId = item.groupId || SKILL_LEARNING_EMPTY_TEXT;
    let sourceGroup = this.otherSourceGroups.find(group => group.groupId === groupId);
    if (!sourceGroup) {
      sourceGroup = {
        groupId,
        groupName: item.groupName,
        groupNameTranslation: item.groupNameTranslation,
        specializations: [],
      };
      this.otherSourceGroups.push(sourceGroup);
    }
    sourceGroup.specializations.push(item);
  }

  private isOwnDepartmentSpecialization(item: SkillSpecializationListItem): boolean {
    const department = this.selectedCatalogDepartment;
    return !!item.groupId && (item.groupId === this.selectedCatalogDepartmentId
      || !!department && item.groupId === department.parentDepartmentId);
  }

  private resetDerivedFolders(): void {
    this.ownSpecializations = [];
    this.crossSpecializations = [];
    this.companySpecializations = [];
    this.otherSourceGroups = [];
    this.activeFolderSpecializations = [];
  }

  private ensureActiveSourceDepartment(): void {
    const activeExists = this.otherSourceGroups.some(
      group => group.groupId === this.activeSourceDepartmentId,
    );
    if (!activeExists) {
      this.activeSourceDepartmentId = this.otherSourceGroups.length
        ? this.otherSourceGroups[SKILL_LEARNING_FIRST_INDEX].groupId
        : null;
    }
  }

  private refreshActiveFolder(): void {
    if (this.activeFolderId === this.folderIds.COMPANY) {
      this.activeFolderSpecializations = this.companySpecializations;
    } else if (this.activeFolderId === this.folderIds.CROSS) {
      this.activeFolderSpecializations = this.crossSpecializations;
    } else if (this.activeFolderId === this.folderIds.OTHER) {
      this.activeFolderSpecializations = this.getActiveOtherSpecializations();
    } else {
      this.activeFolderSpecializations = this.ownSpecializations;
    }
  }

  private getActiveOtherSpecializations(): SkillSpecializationListItem[] {
    const source = this.otherSourceGroups.find(
      group => group.groupId === this.activeSourceDepartmentId,
    );
    return source ? source.specializations : [];
  }

  private loadCreateAccess(): void {
    this.facade.fetchGroups()
      .takeUntil(this.destroy$)
      .subscribe(
        groups => this.handleCreateAccessLoaded(groups),
        () => this.handleCreateAccessError(),
      );
  }

  private handleCreateAccessLoaded(groups: SkillGroup[]): void {
    this.availableGroups = groups || [];
    this.canCreate = this.availableGroups.length > SKILL_LEARNING_FIRST_INDEX;
    this.isCreateAccessLoaded = true;
    const fixedDepartment = this.resolveFixedCatalogDepartment(this.selectedCatalogDepartmentId);
    this.fixedCatalogDepartmentId = fixedDepartment ? fixedDepartment.id : SKILL_LEARNING_EMPTY_TEXT;
    if (fixedDepartment) {
      const shouldReplaceQuery = this.selectedCatalogDepartmentId !== fixedDepartment.id;
      this.applyCatalogDepartmentContext(fixedDepartment.id);
      if (shouldReplaceQuery) {
        this.updateCatalogDepartmentQuery(true);
      }
    } else {
      this.applyCatalogDepartmentContext(this.selectedCatalogDepartmentId);
    }
    this.cdr.markForCheck();
  }

  private handleCreateAccessError(): void {
    this.availableGroups = [];
    this.canCreate = false;
    this.fixedCatalogDepartmentId = SKILL_LEARNING_EMPTY_TEXT;
    this.isCreateAccessLoaded = true;
    this.applyCatalogDepartmentContext(this.selectedCatalogDepartmentId);
    this.cdr.markForCheck();
  }

  private updateCatalogDepartmentQuery(replaceUrl = false): void {
    const path = this.router.url.split(SKILL_URL_QUERY_SEPARATOR)[SKILL_LEARNING_FIRST_INDEX];
    const navigationExtras: NavigationExtras = { queryParams: this.catalogDepartmentQuery };
    if (replaceUrl) {
      navigationExtras.replaceUrl = true;
    }
    this.router.navigate([path], navigationExtras);
  }

  private resolveFixedCatalogDepartment(departmentId: string): SkillGroup | null {
    if (departmentId) {
      return this.availableGroups.find(group => group.id === departmentId) || null;
    }
    if (this.availableGroups.length === SKILL_LEARNING_LEVEL_COUNT.ONE) {
      return this.availableGroups[SKILL_LEARNING_FIRST_INDEX];
    }
    const defaults = this.availableGroups.filter(group => group.defaultDepartmentContext === true);
    return defaults.length === SKILL_LEARNING_LEVEL_COUNT.ONE
      ? defaults[SKILL_LEARNING_FIRST_INDEX] : null;
  }

  private get catalogDepartmentQuery(): Params {
    const query: Params = {};
    if (this.selectedCatalogDepartmentId) {
      query[SKILL_DEPARTMENT_QUERY_PARAM] = this.selectedCatalogDepartmentId;
    }
    return query;
  }

  private syncPendingDeleteItem(): void {
    if (this.pendingDeleteItem) {
      const id = this.pendingDeleteItem.id;
      this.pendingDeleteItem = this.specializations.find(item => item.id === id) || null;
    }
  }

  private handleDeleteSuccess(id: string): void {
    this.specializations = this.specializations.filter(item => item.id !== id);
    this.catalogSpecializations = this.catalogSpecializations.filter(item => item.id !== id);
    this.pendingDeleteItem = null;
    this.isDeleting = false;
    this.deleteErrorKey = SKILL_LEARNING_EMPTY_TEXT;
    this.rebuildFolders();
    this.toastService.success(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_DELETE_SUCCESS'));
    this.cdr.markForCheck();
  }

  private handleDeleteError(error: SkillLearningHttpError): void {
    this.isDeleting = false;
    this.deleteErrorKey = this.resolveDeleteErrorKey(error);
    if (this.deleteErrorKey === SKILL_DEACTIVATION_MESSAGE.CONFLICT) {
      this.reloadCurrentContext();
    }
    this.cdr.markForCheck();
  }

  private resolveDeleteErrorKey(error: SkillLearningHttpError): string {
    if (error && error.status === SKILL_HTTP_STATUS.FORBIDDEN) {
      return SKILL_DEACTIVATION_MESSAGE.DENIED;
    }
    const response = error && error.json ? error.json() : null;
    if (response && response.code === SKILL_CONFIGURATION_ERROR.CONFLICT) {
      return SKILL_DEACTIVATION_MESSAGE.CONFLICT;
    }
    if (response && response.code === SKILL_CONFIGURATION_ERROR.DEACTIVATION_REQUIREMENT_ACTIVE) {
      return SKILL_DEACTIVATION_MESSAGE.REQUIREMENT_ACTIVE;
    }
    if (response && response.code === SKILL_CONFIGURATION_ERROR.DEACTIVATION_ACTIVE_TASK) {
      return SKILL_DEACTIVATION_MESSAGE.ACTIVE_TASK;
    }
    return SKILL_DEACTIVATION_MESSAGE.SAVE_ERROR;
  }

  private handleFillTaskSuccess(): void {
    this.toastService.success(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_FILL_TASK_SUCCESS'));
    this.reloadCurrentContext();
  }

  private handleFillTaskError(): void {
    this.toastService.error(this.translate.instant('SKILL_LEARNING_ADMIN_LIST_FILL_TASK_ERROR'));
    this.cdr.markForCheck();
  }

  private reloadCurrentContext(): void {
    if (this.canShowCatalog && this.selectedCatalogDepartmentId) {
      this.loadSelectedDepartmentSources();
    } else {
      this.loadSpecializations();
    }
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/specialization-list-container/specialization-list-container.component.ts